import { translate, useLocale } from "../../localization";
import React, { useState } from "react";
import UploadElementDialog from "./UploadElementDialog";
import { AppButton } from "../ui";
import useElementUploadWorkflow from "./useElementUploadWorkflow";
import DataObjectMetadataDialog from "./DataObjectMetadataEditor";
import {
  canUploadReplacementModel,
  isElementClone,
} from "../../utility/elementIdentity";

const NewElementUploader = ({ onNewElementCreated, initialElementId, initialElementData }) => {
  useLocale();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [metadataElement, setMetadataElement] = useState(null);
  const { uploadSingleElement } = useElementUploadWorkflow({
    initialElementId,
    initialElementData,
  });
  const isReplacementUpload = Boolean(initialElementId);
  const replacementUploadAllowed =
    !isReplacementUpload ||
    canUploadReplacementModel(initialElementId, initialElementData);
  const replacementUploadBlocked =
    isReplacementUpload && !replacementUploadAllowed;
  const blockedClone = isElementClone(initialElementData);

  const handleOpenDialog = () => {
    if (replacementUploadBlocked) return;
    setUploadDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
  };

  const handleUpload = async ({ name, description, file, optimization, onProgress }) => {
    if (!file || replacementUploadBlocked) {
      return;
    }

    try {
      const createdElement = await uploadSingleElement({
        file,
        name,
        description,
        optimization,
        onProgress,
      });

      // Call parent's callback, if provided, to update the list.
      if (onNewElementCreated && !initialElementId) {
        onNewElementCreated(createdElement);
      }

      setMetadataElement(createdElement);
      handleCloseDialog();
      return createdElement;
    } catch (error) {
      if (error.uploadedElement) {
        setMetadataElement({ ...error.uploadedElement, uploadError: error.message });
        handleCloseDialog();
      }
      console.error("Error uploading new element:", error);
      throw error;
    }
  };

  return (
    <div className="elementModelUploadControl">
      <AppButton
        onClick={handleOpenDialog}
        disabled={replacementUploadBlocked}
      >
        {isReplacementUpload ? translate("Upload New Model") : translate("Upload New Element")}
      </AppButton>
      {replacementUploadBlocked && (
        <span className="elementModelUploadNotice" role="status">
          {blockedClone
            ? translate("Model uploads are unavailable for cloned elements.")
            : translate("Model upload is unavailable because this element's source identity could not be verified.")}
        </span>
      )}
      <UploadElementDialog
        open={uploadDialogOpen}
        onClose={handleCloseDialog}
        onUpload={handleUpload}
      />
      <DataObjectMetadataDialog element={metadataElement} onClose={() => setMetadataElement(null)} />
    </div>
  );
};

export default NewElementUploader;
